import React from "react";
import { useNavigate } from "react-router-dom";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import AdminApiTable from "@/modules/racket_lab_admin/AdminApiTable";
import RLShopServiceModeBadge from "@/modules/racket_lab_admin/RLShopServiceModeBadge";
import useAdminApi from "@/hooks/useAdminApi";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminShops — Sub-sprint A read-only list of Racket Lab shops.
 *
 * Driven by GET /api/admin/racket-lab/shops. Click on a row opens
 * the detail page (also read-only in v0).
 *
 * QUICK 1.C i18n migration: page header, counter, columns and empty
 * state resolved via `racketLab.shops.*`. Route and `data-testid`s
 * unchanged.
 */
const RLAdminShops = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { data, loading, error } = useAdminApi("/admin/racket-lab/shops");

  const rows = Array.isArray(data) ? data : data?.items || data?.shops || [];

  const columns = [
    {
      key: "name",
      label: t("shops.columns.name"),
      render: (row) => (
        <span data-testid={`rl-shop-row-name-${row.id}`} className="font-medium">
          {row.name || "—"}
        </span>
      ),
    },
    { key: "country", label: t("shops.columns.country") },
    {
      key: "service_mode",
      label: t("shops.columns.service_mode"),
      render: (row) => <RLShopServiceModeBadge mode={row.settings?.service_mode} size="xs" />,
    },
    {
      key: "organisation_id",
      label: t("shops.columns.organisation"),
      render: (row) => (
        <code className="text-[10px] text-slate-500">
          {row.organisation_id?.slice(0, 12) || "—"}
        </code>
      ),
    },
    {
      key: "id",
      label: t("shops.columns.shop_id"),
      render: (row) => (
        <code className="text-[10px] text-slate-500">
          {row.id?.slice(0, 12) || "—"}
        </code>
      ),
    },
  ];

  return (
    <div data-testid="rl-admin-shops-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-slate-900">
          {t("shops.page_header")}
        </h1>
        <p className="text-xs text-slate-500">
          {t(rows.length === 1 ? "shops.counter" : "shops.counter_plural", { count: rows.length })}
        </p>
      </header>

      <AdminApiTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        emptyLabel={t("shops.empty")}
        onRowClick={(row) =>
          navigate(`/admin/racket-lab/workforce/shops/${row.id}`)
        }
        dataTestId="rl-shops-table"
      />
    </div>
  );
};

export default RLAdminShops;
